import React, { useEffect, useState, useCallback } from "react";
import { useAgents } from "@/lib/useAgents";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Target,
  Building2,
  User,
  Briefcase,
  Mail,
  Clock,
  CheckSquare2,
  Phone,
  Smartphone,
  Globe,
  MapPin,
  TrendingUp,
  Flame,
  ArrowRight,
  ExternalLink,
  DollarSign,
  Calendar,
  AlertCircle,
  Sparkles,
  Radio,
  Tag,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { EntityDetailLayout, type TabItem } from "@/components/entity-detail/EntityDetailLayout";
import { EntityHeader } from "@/components/entity-detail/EntityHeader";
import { EntityInfoSidebar, type InfoSection } from "@/components/entity-detail/EntityInfoSidebar";
import { WhatsAppWidget } from "@/components/entity-detail/WhatsAppWidget";
import { TasksWidget } from "@/components/entity-detail/TasksWidget";
import { ActivityTimeline } from "@/components/entity-detail/ActivityTimeline";
import { formatINR, humanizeCode } from "@/lib/utils";
import type { Lead360 } from "@/types";

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agents = useAgents();
  const { success, error: toastError } = useToast();
  const { hasPermission } = useAuth();

  const [data, setData] = useState<Lead360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // WhatsApp dialog
  const [waDialogOpen, setWaDialogOpen] = useState(false);

  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  // Convert dialog
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({
    create_deal: true,
    deal_name: "",
    deal_amount: 0,
    expected_close_date: "",
  });
  const [convertLoading, setConvertLoading] = useState(false);

  const fetch360 = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/Lead/360/${id}`);
      const payload = unwrapObject<Lead360>(res);
      setData(payload);
      if (payload?.lead) {
        setConvertForm({
          create_deal: true,
          deal_name: `${payload.lead.company_name} - Deal`,
          deal_amount: Number(payload.lead.expected_budget || payload.lead.estimated_value || 0),
          expected_close_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
        });
      }
    } catch (err: any) {
      toastError("Failed to load lead", err.message || "Lead not found");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toastError]);

  useEffect(() => {
    fetch360();
  }, [fetch360]);

  if (loading || !data) {
    return (
      <div className="p-6 space-y-6">
        <FormSkeleton />
      </div>
    );
  }

  const { lead, converted_account, converted_contact, converted_deal, tasks, whatsapp, inquiries = [] } = data;
  const leadName = `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.company_name;
  const phoneNum = lead.mobile || lead.phone;
  const isConverted = lead.lead_status === "CONVERTED" || Boolean(lead.converted_at);

  const handleConvertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setConvertLoading(true);
      await api.post(`/api/Convert/Lead/${id}`, {
        create_deal: convertForm.create_deal,
        deal_name: convertForm.deal_name.trim() || undefined,
        deal_amount: Number(convertForm.deal_amount) || undefined,
        expected_close_date: convertForm.expected_close_date ? new Date(convertForm.expected_close_date).toISOString() : undefined,
      });
      success("Lead Converted", "Successfully converted into an Account, Contact, and Opportunity.");
      setConvertOpen(false);
      fetch360();
    } catch (err: any) {
      toastError("Conversion Failed", err.message || "Could not convert lead");
    } finally {
      setConvertLoading(false);
    }
  };

  const getPriorityBadgeVariant = (priority?: string) => {
    switch (priority) {
      case "HOT":
        return { label: "HOT LEAD", variant: "default" as const, className: "bg-rose-500 text-white" };
      case "WARM":
        return { label: "WARM LEAD", variant: "default" as const, className: "bg-amber-500 text-white" };
      default:
        return { label: "COLD LEAD", variant: "outline" as const, className: "text-blue-500 border-blue-500/30" };
    }
  };

  const priorityBadge = getPriorityBadgeVariant(lead.priority);
  const openTasksCount = tasks.filter((t) => t.task_status === "OPEN").length;

  const sidebarSections: InfoSection[] = [
    {
      title: "Contact Information",
      fields: [
        {
          icon: Smartphone,
          label: "Mobile / WhatsApp",
          value: lead.mobile,
          href: lead.mobile ? `tel:${lead.mobile}` : undefined,
          copyable: true,
        },
        {
          icon: Phone,
          label: "Office Phone",
          value: lead.phone,
          href: lead.phone ? `tel:${lead.phone}` : undefined,
          copyable: true,
        },
        {
          icon: Mail,
          label: "Email",
          value: lead.email,
          href: lead.email ? `mailto:${lead.email}` : undefined,
          copyable: true,
        },
        {
          icon: User,
          label: "Designation",
          value: lead.job_title,
        },
      ],
    },
    {
      title: "Company & Location",
      fields: [
        {
          icon: Building2,
          label: "Company Name",
          value: lead.company_name,
        },
        {
          icon: Globe,
          label: "Website",
          value: lead.website,
          href: lead.website ? (lead.website.startsWith("http") ? lead.website : `https://${lead.website}`) : undefined,
          isExternal: true,
        },
        {
          icon: MapPin,
          label: "City / State",
          value: [lead.city, lead.state].filter(Boolean).join(", "),
        },
      ],
    },
    {
      title: "Requirements & Budget",
      fields: [
        {
          icon: DollarSign,
          label: "Expected Budget",
          value: Number(lead.expected_budget) > 0 ? formatINR(lead.expected_budget!) : undefined,
        },
        {
          icon: Sparkles,
          label: "Product Interest",
          value: lead.product_interest,
        },
        {
          icon: Calendar,
          label: "Timeline",
          value: lead.expected_timeline,
        },
      ],
    },
    {
      title: "Textile Requirement",
      fields: [
        { icon: Sparkles, label: "Product Type", value: humanizeCode(lead.product_type) },
        {
          icon: Sparkles,
          label: "Quantity",
          value: Number(lead.required_qty) > 0 ? `${Number(lead.required_qty).toLocaleString("en-IN")} ${lead.qty_unit || ""}`.trim() : undefined,
        },
        {
          icon: DollarSign,
          label: "Target Price / Unit",
          value: Number(lead.target_price) > 0 ? formatINR(lead.target_price!) : undefined,
        },
        { icon: Sparkles, label: "End Use", value: humanizeCode(lead.end_use) },
        { icon: Calendar, label: "Required By", value: lead.required_by ? new Date(lead.required_by).toLocaleDateString("en-IN") : undefined },
        { icon: Sparkles, label: "Agent / Broker", value: lead.agent_id ? agents.find((a) => a.id === lead.agent_id)?.name || "Agent (inactive)" : undefined },
      ],
    },
  ];

  if (lead.utm_source || lead.utm_campaign || lead.first_inquiry_at || (lead.inquiry_count && lead.inquiry_count > 1)) {
    sidebarSections.push({
      title: "Attribution & Inbound",
      fields: [
        {
          icon: Radio,
          label: "Inquiry Count",
          value: `${lead.inquiry_count || 1} inquiries received`,
        },
        lead.first_inquiry_at
          ? {
              icon: Clock,
              label: "First Inquiry",
              value: new Date(lead.first_inquiry_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }),
            }
          : undefined,
        lead.last_inquiry_at && lead.inquiry_count && lead.inquiry_count > 1
          ? {
              icon: Clock,
              label: "Last Inquiry",
              value: new Date(lead.last_inquiry_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }),
            }
          : undefined,
        lead.utm_source
          ? {
              icon: Tag,
              label: "UTM Source / Medium",
              value: `${lead.utm_source}${lead.utm_medium ? ` / ${lead.utm_medium}` : ""}`,
            }
          : undefined,
        lead.utm_campaign
          ? {
              icon: Tag,
              label: "UTM Campaign",
              value: lead.utm_campaign,
            }
          : undefined,
      ].filter(Boolean) as any,
    });
  }

  // Overview Tab Component
  const OverviewTab = (
    <div className="space-y-6">
      {/* Converted Callout Banner (if already converted) */}
      {isConverted && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold text-sm">
            <Sparkles className="size-4" />
            <span>This Lead Has Been Converted</span>
          </div>
          <p className="text-xs text-muted-foreground">
            This lead was successfully converted on{" "}
            {lead.converted_at ? new Date(lead.converted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "record"}.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
            {converted_account && (
              <Link
                to={`/accounts/${converted_account.id}`}
                className="bg-card border border-border/80 rounded-xl p-3 hover:border-emerald-500/50 transition-colors space-y-1 block"
              >
                <div className="flex items-center justify-between text-xs text-muted-foreground font-semibold">
                  <span>ACCOUNT</span>
                  <ExternalLink className="size-3" />
                </div>
                <div className="text-sm font-bold text-foreground truncate">{converted_account.name}</div>
              </Link>
            )}

            {converted_contact && (
              <Link
                to={`/contacts/${converted_contact.id}`}
                className="bg-card border border-border/80 rounded-xl p-3 hover:border-emerald-500/50 transition-colors space-y-1 block"
              >
                <div className="flex items-center justify-between text-xs text-muted-foreground font-semibold">
                  <span>CONTACT</span>
                  <ExternalLink className="size-3" />
                </div>
                <div className="text-sm font-bold text-foreground truncate">
                  {converted_contact.first_name} {converted_contact.last_name || ""}
                </div>
              </Link>
            )}

            {converted_deal && (
              <Link
                to={`/deals/${converted_deal.id}`}
                className="bg-card border border-border/80 rounded-xl p-3 hover:border-emerald-500/50 transition-colors space-y-1 block"
              >
                <div className="flex items-center justify-between text-xs text-muted-foreground font-semibold">
                  <span>OPPORTUNITY</span>
                  <ExternalLink className="size-3" />
                </div>
                <div className="text-sm font-bold text-foreground truncate">{converted_deal.name}</div>
                <div className="text-xs font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatINR(converted_deal.amount)}
                </div>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Disqualified Banner */}
      {lead.lead_status === "DISQUALIFIED" && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-4 flex items-start gap-3">
          <AlertCircle className="size-5 text-rose-500 shrink-0 mt-0.5" />
          <div className="space-y-1 text-xs">
            <h4 className="font-semibold text-rose-600 dark:text-rose-400">Lead Disqualified</h4>
            <p className="text-foreground/80">
              Reason: {lead.disqualify_reason || "Not specified"}. {lead.disqualify_notes}
            </p>
          </div>
        </div>
      )}

      {/* Metric Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs space-y-1">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Status</span>
          <div className="text-base sm:text-lg font-bold tracking-tight text-foreground truncate">{lead.lead_status}</div>
          <p className="text-[11px] text-muted-foreground">Source: {lead.source}</p>
        </div>

        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs space-y-1">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Lead Score</span>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{lead.score || 0} / 100</div>
          <p className="text-[11px] text-muted-foreground">Priority: {lead.priority || "WARM"}</p>
        </div>

        <div className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs space-y-1">
          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Est. Budget</span>
          <div className="text-lg sm:text-xl font-bold font-mono tracking-tight text-foreground">
            {Number(lead.expected_budget) > 0 ? formatINR(lead.expected_budget!) : "Not given"}
          </div>
          <p className="text-[11px] text-muted-foreground truncate">{lead.product_interest || "General"}</p>
        </div>

        <div
          onClick={() => setActiveTab("tasks")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Pending Tasks</span>
            <div className="size-7 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <CheckSquare2 className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{openTasksCount}</div>
          <p className="text-[11px] text-muted-foreground">{tasks.length} total tasks</p>
        </div>
      </div>

      {/* Activity Timeline */}
      <ActivityTimeline
        whatsapp={whatsapp}
        tasks={tasks}
        onOpenWhatsApp={() => setWaDialogOpen(true)}
      />
    </div>
  );

  const tabs: TabItem[] = [
    { id: "overview", label: "Overview", icon: TrendingUp, content: OverviewTab },
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: WhatsAppIcon as any,
      count: whatsapp.length,
      content: (
        <WhatsAppWidget
          threads={whatsapp}
          onOpenChat={() => setWaDialogOpen(true)}
          onStartChat={phoneNum ? () => setWaDialogOpen(true) : undefined}
          canSend={hasPermission("whatsapp.send")}
        />
      ),
    },
    {
      id: "tasks",
      label: "Tasks",
      icon: CheckSquare2,
      count: openTasksCount,
      content: (
        <TasksWidget
          tasks={tasks}
          relatedType="LEAD"
          relatedId={lead.id}
          relatedName={leadName}
          onRefresh={fetch360}
          canCreate={hasPermission("activity.create")}
        />
      ),
    },
    {
      id: "timeline",
      label: "Timeline",
      icon: Clock,
      content: (
        <ActivityTimeline
          whatsapp={whatsapp}
          tasks={tasks}
          onOpenWhatsApp={() => setWaDialogOpen(true)}
        />
      ),
    },
    {
      id: "inquiries",
      label: "Inbound Inquiries",
      icon: Radio,
      count: inquiries.length,
      content: (
        <div className="space-y-4">
          {inquiries.length === 0 ? (
            <div className="bg-card border border-dashed rounded-2xl p-8 text-center text-xs text-muted-foreground">
              No inbound intake records logged for this lead.
            </div>
          ) : (
            <div className="space-y-3">
              {inquiries.map((inq) => (
                <div key={inq.id} className="bg-card border rounded-xl p-4 space-y-2.5">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-foreground">
                        {inq.connection?.name || "Direct Intake Channel"}
                      </span>
                      <Badge variant="outline" className="text-[10px] py-0 h-5">
                        {inq.inbox_status}
                      </Badge>
                    </div>
                    <span className="text-muted-foreground font-mono text-[11px]">
                      {new Date(inq.received_at).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  {inq.match_reason && (
                    <div className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">Match Action: </span>
                      {inq.match_reason}
                    </div>
                  )}
                  {inq.normalized?.requirement_notes && (
                    <div className="bg-muted/40 p-2.5 rounded-lg text-xs text-foreground/90 font-sans whitespace-pre-wrap border">
                      {inq.normalized?.requirement_notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <EntityDetailLayout
        breadcrumbs={[
          { label: "Leads", href: "/leads" },
          { label: leadName },
        ]}
        header={
          <EntityHeader
            type="LEAD"
            title={leadName}
            subtitle={lead.company_name !== leadName ? `Company: ${lead.company_name}` : undefined}
            icon={Target}
            avatarBg="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
            badges={[
              priorityBadge,
              { label: lead.lead_status, variant: isConverted ? "default" : "secondary", className: isConverted ? "bg-emerald-500 text-white" : "" },
            ]}
            owner={lead.owner ? { id: lead.owner.id, name: `${lead.owner.first_name} ${lead.owner.last_name || ""}`.trim() } : null}
            createdOn={lead.created_on}
            onEdit={hasPermission("lead.update") ? () => navigate(`/leads/${lead.id}/edit`) : undefined}
            onWhatsApp={phoneNum && hasPermission("whatsapp.send") ? () => setWaDialogOpen(true) : undefined}
            onEmail={lead.email ? () => window.open(`mailto:${lead.email}`) : undefined}
            onAddTask={() => setTaskDialogOpen(true)}
            hasWhatsApp={Boolean(phoneNum && hasPermission("whatsapp.send"))}
            hasEmail={Boolean(lead.email)}
            extraActions={
              !isConverted && hasPermission("lead.convert") && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConvertOpen(true)}
                  className="gap-1.5 border-emerald-500/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 font-semibold"
                >
                  <Sparkles className="size-3.5" />
                  <span>Convert Lead</span>
                </Button>
              )
            }
            backUrl="/leads"
          />
        }
        sidebar={
          <EntityInfoSidebar
            sections={sidebarSections}
            notes={lead.requirement_notes}
            tags={lead.tags}
          />
        }
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Convert Lead Dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={handleConvertSubmit}>
            <DialogHeader>
              <DialogTitle>Convert Lead to Opportunity</DialogTitle>
              <DialogDescription>
                Converts this lead into an Account, Contact, and Sales Deal.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4 text-xs sm:text-sm">
              <div className="p-3.5 bg-muted/40 rounded-xl space-y-1.5 text-xs sm:text-sm border border-border">
                <div>Account created: <strong>{lead.company_name}</strong></div>
                <div>Contact created: <strong>{lead.first_name} {lead.last_name || ""}</strong></div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="conv_deal_name" className="text-xs sm:text-sm font-semibold">Opportunity / Deal Name *</Label>
                <Input
                  id="conv_deal_name"
                  value={convertForm.deal_name}
                  onChange={(e) => setConvertForm((p) => ({ ...p, deal_name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="conv_amount" className="text-xs sm:text-sm font-semibold">Deal Value (₹)</Label>
                  <Input
                    id="conv_amount"
                    type="number"
                    value={convertForm.deal_amount}
                    onChange={(e) => setConvertForm((p) => ({ ...p, deal_amount: Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="conv_close" className="text-xs sm:text-sm font-semibold">Target Close Date</Label>
                  <Input
                    id="conv_close"
                    type="date"
                    value={convertForm.expected_close_date}
                    onChange={(e) => setConvertForm((p) => ({ ...p, expected_close_date: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setConvertOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={convertLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {convertLoading ? "Converting..." : "Complete Conversion"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Chat Dialog */}
      {phoneNum && (
        <WhatsAppChatDialog
          open={waDialogOpen}
          onOpenChange={(v) => {
            setWaDialogOpen(v);
            if (!v) fetch360();
          }}
          phone={phoneNum}
          name={leadName}
          leadId={lead.id}
        />
      )}

      {/* Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        defaultRelated={{
          type: "LEAD",
          id: lead.id,
          name: leadName,
        }}
        onSaved={() => {
          setTaskDialogOpen(false);
          fetch360();
        }}
      />
    </>
  );
}
